/**
 * Role Management - Hierarchical roles with colors
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Shield } from 'lucide-react';
import axios from 'axios';

const PRESET_COLORS = ['#007AFF','#34C759','#FF375F','#FF9500','#C77DFF','#FF9A3C','#4ECDC4','#FF6B9D'];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.40)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl p-6"
        style={{ background: 'rgba(255,255,255,0.96)', border: '1px solid rgba(0,122,255,0.20)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-n-800 text-base">{title}</h3>
          <button onClick={onClose} className="text-n-500 hover:text-n-700"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#007AFF', description: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/roles');
      setRoles(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', color: '#007AFF', description: '' });
    setErr('');
    setModal('create');
  };

  const openEdit = (r) => {
    setSelected(r);
    setForm({ name: r.name, color: r.color, description: r.description || '' });
    setErr('');
    setModal('edit');
  };

  const openDelete = (r) => { setSelected(r); setModal('delete'); };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es requerido'); return; }
    setSaving(true);
    try {
      if (modal === 'create') {
        await axios.post('/api/admin/roles', form);
      } else {
        await axios.put(`/api/admin/roles/${selected.id}`, form);
      }
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  // ── Permission matrix (Feature #9) ─────────────────────────────────────────
  const FEATURE_PERMS = [
    { key: 'ptt',      label: 'PTT',      hue: '#007AFF' },
    { key: 'map',      label: 'Map',      hue: '#34C759' },
    { key: 'missions', label: 'Misiones', hue: '#FF9F0A' },
    { key: 'replay',   label: 'Replay',   hue: '#AF52DE' },
    { key: 'admin',    label: 'Admin',    hue: '#FF2D55' },
  ];

  const togglePerm = async (role, key) => {
    const perms = { ...(role.permissions || {}) };
    perms[key] = !perms[key];
    try {
      await axios.put(`/api/admin/roles/${role.id}`, {
        name: role.name, color: role.color, description: role.description,
        permissions: perms,
      });
      setRoles(prev => prev.map(r => r.id === role.id ? { ...r, permissions: perms } : r));
    } catch (e) { console.warn('[Roles] toggle perm', e); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`/api/admin/roles/${selected.id}`);
      await load();
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Error al eliminar');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-n-800">Gestión de Roles</h1>
          <p className="text-sm text-n-500 mt-0.5">Define roles jerárquicos para control de acceso</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-n-800 transition-all hover:opacity-90"
          style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
          <Plus size={15} /> Nuevo Rol
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-n-500 text-sm">Cargando...</div>
      ) : roles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-n-500 text-sm">
          <Shield size={32} className="mb-3 opacity-30" />
          <p>No hay roles configurados</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {roles.map(r => (
            <div key={r.id} className="flex items-center gap-4 px-5 py-4 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.82)', border: '1px solid rgba(0,0,0,0.05)' }}>
              {/* Color badge */}
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: r.color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-n-800 text-sm">{r.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-mono"
                    style={{ background: r.color + '22', color: r.color, border: `1px solid ${r.color}44` }}>
                    #{r.id}
                  </span>
                </div>
                {r.description && <p className="text-xs text-n-500 mt-0.5 truncate">{r.description}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => openEdit(r)}
                  className="p-2 rounded-lg text-n-500 hover:text-s-blue hover:bg-s-blue/10 transition-all">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => openDelete(r)}
                  className="p-2 rounded-lg text-n-500 hover:text-s-red hover:bg-s-red/10 transition-all">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Permission Matrix */}
      {!loading && roles.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-bold text-n-800 uppercase tracking-wider mb-3">Matriz de permisos</h2>
          <div className="rounded-lg overflow-x-auto" style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(0,0,0,0.05)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <th className="text-left px-4 py-2 text-xs font-bold uppercase text-n-500">Rol</th>
                  {FEATURE_PERMS.map(p => (
                    <th key={p.key} className="text-center px-4 py-2 text-xs font-bold uppercase" style={{ color: p.hue }}>
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roles.map(r => {
                  const all = (r.permissions || {}).all === true;
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: r.color }} />
                          <span className="font-semibold text-n-800">{r.name}</span>
                        </div>
                      </td>
                      {FEATURE_PERMS.map(p => {
                        const checked = all || (r.permissions || {})[p.key] === true;
                        return (
                          <td key={p.key} className="text-center px-4 py-2">
                            <button
                              disabled={all}
                              onClick={() => togglePerm(r, p.key)}
                              className="w-9 h-5 rounded-full transition-all relative inline-block"
                              style={{
                                background: checked ? p.hue : '#E5E5EA',
                                opacity: all ? 0.5 : 1,
                                cursor: all ? 'not-allowed' : 'pointer',
                              }}
                              title={all ? 'Rol con permiso global (all)' : (checked ? 'Activo — click para deshabilitar' : 'Inactivo — click para habilitar')}
                            >
                              <span
                                className="absolute top-0.5 transition-all rounded-full bg-white shadow-apple-xs"
                                style={{
                                  left: checked ? '18px' : '2px',
                                  width: 16, height: 16,
                                }}
                              />
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-n-500 mt-2 italic">
            Los roles con permiso global ("all": true) tienen acceso a todo y no pueden modificarse aquí.
          </p>
        </div>
      )}

      {/* Create / Edit Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal title={modal === 'create' ? 'Nuevo Rol' : 'Editar Rol'} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Nombre</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="ej. technician"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Descripción</label>
              <input
                className="w-full px-4 py-2.5 rounded-xl text-sm text-n-800 placeholder-n-400 outline-none"
                style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,122,255,0.20)' }}
                placeholder="Descripción del rol"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-n-600 uppercase tracking-wider mb-1.5">Color</label>
              <div className="flex items-center gap-3">
                <div className="flex gap-2 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className="w-7 h-7 rounded-full transition-all"
                      style={{
                        background: c,
                        outline: form.color === c ? `2px solid white` : 'none',
                        outlineOffset: '2px',
                      }} />
                  ))}
                </div>
                <input type="color" value={form.color}
                  onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                  className="w-10 h-8 rounded cursor-pointer border-0 bg-transparent" />
              </div>
            </div>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700 hover:border-slate-500 transition-all">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-800 disabled:opacity-50 transition-all"
                style={{ background: 'linear-gradient(135deg,#007AFF,#5856D6)' }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && (
        <Modal title="Eliminar Rol" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-n-600">
              ¿Eliminar el rol <span className="font-bold" style={{ color: selected?.color }}>{selected?.name}</span>?
              Los usuarios con este rol perderán su asignación.
            </p>
            {err && <p className="text-xs text-s-red">{err}</p>}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-n-600 border border-slate-700 hover:border-slate-500 transition-all">
                Cancelar
              </button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg,#FF375F,#FF3B30)' }}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
